import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { getArticleBySlug } from "@/mocks/articles";

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || "");

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-semibold text-foreground-950 mb-2">
              文章未找到
            </h1>
            <Link
              to="/articles"
              className="text-primary-600 hover:text-primary-700 text-sm underline whitespace-nowrap cursor-pointer"
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "精选文章", to: "/articles" },
            { label: article.title },
          ]}
        />

        {/* Article Header */}
        <section className="w-full bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
              <span className="text-xs text-foreground-500">·</span>
              <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
              <span className="text-xs text-foreground-500">·</span>
              <span className="text-xs text-foreground-500">{article.author}</span>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              <strong>{article.title}</strong>
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
          </div>
        </section>

        {/* Featured Image */}
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 -mt-0">
          <div className="aspect-[16/9] rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>

        {/* Article Content */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <article
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": article.title,
                  "description": article.excerpt,
                  "image": article.image,
                  "datePublished": article.date,
                  "author": {
                    "@type": "Person",
                    "name": article.author,
                  },
                }),
              }}
            />
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <Link
                to="/articles"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors no-underline cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                返回文章列表
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}